import {
    GridPreferencePanelsValue,
    GridRowCount,
    GridRowModes,
    GridSelectedRowCount,
    GridToolbarColumnsButton,
    GridToolbarContainer,
    GridToolbarFilterButton,
    GridToolbarQuickFilter,
    gridPreferencePanelStateSelector,
    useGridApiContext,
} from "@mui/x-data-grid-premium";
import React, { memo, useCallback } from "react";
import { ITableState } from "../EntityTable";
import "../EntityTable.css";
import { Box, IconButton, Menu, MenuItem, TablePagination, TablePaginationProps, Typography, useTheme } from "@mui/material";
import { MirageTheme } from "@mirage/theme/MuiMirage.module";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { NavigateOptions, To } from "react-router-dom";
import { LoadingButton } from "@mui/lab";
import { useStableNavigate } from "@mirage/hooks/useStableNavigate";
import { IconDefinition } from "@fortawesome/pro-solid-svg-icons";
import { useState } from "react";

export interface CustomGridToolbarProps {
    rowMode: "view" | GridRowModes;
    totalRecords: number;
    setTableState: React.Dispatch<React.SetStateAction<ITableState>>;
    anchorEl: HTMLElement | null;
    setAnchorEl: (el: HTMLElement | null) => void;
    clearPreferences: () => Promise<void>;
    tableTitle?: string;
    options?: CustomGridToolbarOptions;
    paginationProps: TablePaginationProps;
    showAll?: boolean;
    showPagination?: boolean;
    quickSearch?: boolean;
}

const CustomGridToolbar: React.FC<CustomGridToolbarProps> = memo(
    ({
        options,
        paginationProps,
        setTableState,
        totalRecords,
        tableTitle,
        showAll = false,
        quickSearch = false,
        showPagination = true,
        anchorEl,
        setAnchorEl,
        clearPreferences,
    }) => {
        return (
            <GridToolbarContainer className="entityTable__toolbar">
                <Actions setTableState={setTableState} options={options} anchorEl={anchorEl} setAnchorEl={setAnchorEl} clearPreferences={clearPreferences} />
                {quickSearch ? <GridToolbarQuickFilter autoFocus debounceMs={500} /> : null}
                <EntityTableTitle tableTitle={tableTitle} />
                <Pagination paginationProps={paginationProps} totalRecords={totalRecords} showAll={showAll} showPagination={showPagination} />
            </GridToolbarContainer>
        );
    }
);

const Pagination = memo(
    ({ totalRecords, paginationProps, showAll, showPagination }: Pick<CustomGridToolbarProps, "totalRecords" | "paginationProps" | "showAll" | "showPagination">) => {
        const { current } = useGridApiContext();
        if (!totalRecords || !showPagination) {
            return <div className="entityTable__toolbar__pagination-empty"></div>;
        }
        if (showAll) {
            const rowSelectionModelCount = current.getSelectedRows();
            return (
                <Box flex={1} display={"flex"} justifyContent={"flex-end"}>
                    {rowSelectionModelCount.size ? <GridSelectedRowCount selectedRowCount={rowSelectionModelCount.size} /> : null}
                    <GridRowCount rowCount={totalRecords} visibleRowCount={totalRecords} />
                </Box>
            );
        }
        return <TablePagination key="grid-pagination-toolbar" {...paginationProps} className="entityTable__toolbar__pagination" />;
    }
);

const EntityTableTitle = memo(({ tableTitle }: Pick<CustomGridToolbarProps, "tableTitle">) => {
    if (tableTitle) {
        return (
            <Typography variant="h6" className="entityTable__toolbar__title">
                {tableTitle}
            </Typography>
        );
    }
    return null;
});

const Actions = memo((props: Pick<CustomGridToolbarProps, "options" | "setTableState" | "anchorEl" | "setAnchorEl" | "clearPreferences">) => {
    const { options, setTableState, anchorEl, setAnchorEl, clearPreferences } = props;
    const currentTheme = useTheme();
    const isPublicOnlyMode = !!currentTheme.entityTable?.publicOnly;
    const showColumnsButton = !options?.disableColumns && !isPublicOnlyMode;
    const showFilterButton = !options?.disableFilter && !isPublicOnlyMode;
    const showOptionsButton = !options?.disableOptions;
    const showNewRecordButton = !!options?.newRecord;
    const showCustomToolbarActionButtons = !!options?.customButtons;
    const placeholder = <div className="entityTable__toolbar__placeholder"></div>;
    const apiRef = useGridApiContext();
    const handleColumnsButtonOnClick = useCallback(() => {
        const columnHeaderEl = apiRef.current?.rootElementRef?.current?.querySelector(".MuiDataGrid-columnHeaders") as HTMLElement;
        setAnchorEl(columnHeaderEl as HTMLElement);
    }, [apiRef, setAnchorEl]);
    const handleFilterButtonOnClick = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
            event.preventDefault();
            event.stopPropagation();
            const preferencePanelState = gridPreferencePanelStateSelector(apiRef.current.state);
            if (anchorEl?.id === event.currentTarget.id && preferencePanelState.openedPanelValue === GridPreferencePanelsValue.filters) {
                apiRef.current.hideFilterPanel();
                return;
            }
            const columnHeaderEl = apiRef.current.rootElementRef?.current?.querySelector(".MuiDataGrid-columnHeaders") as HTMLElement;
            columnHeaderEl.id = "gridFilterButton";
            setAnchorEl(columnHeaderEl);
            apiRef.current.showFilterPanel();
        },
        [anchorEl, apiRef, setAnchorEl]
    );

    if (!showColumnsButton && !showFilterButton && !showNewRecordButton && !showOptionsButton && !showCustomToolbarActionButtons) {
        return null;
    }

    return (
        <div className="entityTable__toolbar__actions">
            {showNewRecordButton ? <NewRecordButton key="grid-new-record-button-toolbar" setTableState={setTableState} {...options?.newRecord} /> : placeholder}
            {showColumnsButton ? <GridToolbarColumnsButton key="grid-toolbar-columns-button" onClick={handleColumnsButtonOnClick} /> : placeholder}
            {showFilterButton ? <FilterButton disableFilter={options?.disableFilter} handleClick={handleFilterButtonOnClick} /> : placeholder}
            {showOptionsButton ? <OptionsButton clearPreferences={clearPreferences} /> : placeholder}
            {showCustomToolbarActionButtons ? <CustomToolbarActionButtons customButtons={options?.customButtons} /> : placeholder}
        </div>
    );
});

const CustomToolbarActionButtons: React.FC<{ customButtons?: JSX.Element[] | undefined }> = memo(({ customButtons }) => {
    return (
        <div style={{ display: "flex" }}>
            {customButtons?.map((btn, idx) => (
                <div key={idx}>{btn}</div>
            ))}
        </div>
    );
});

const FilterButton = memo(({ disableFilter, handleClick }: { disableFilter?: boolean; handleClick: (event: React.MouseEvent<HTMLElement>) => void }) => {
    if (!disableFilter) {
        return (
            <GridToolbarFilterButton
                key="grid-toolbar-filter-button"
                componentsProps={{
                    button: {
                        onClickCapture: handleClick,
                        id: "gridFilterButton",
                    },
                }}
            />
        );
    }
    return null;
});

const NewRecordButton: React.FC<NewRecordButtonProps> = ({ setTableState, inline, panelType, navigate, onClick, disabled, caption, isLoading = false, icon }) => {
    const buttonIcon = icon ? icon : MirageTheme.icons.plusIcon;
    const navigateTo = useStableNavigate();
    if (inline || panelType || !!navigate || onClick) {
        return (
            <LoadingButton color="primary" disabled={disabled} startIcon={<FontAwesomeIcon icon={buttonIcon} />} onClick={onButtonClicked} loading={isLoading}>
                {caption || "Add record"}
            </LoadingButton>
        );
    }
    return null;

    function onButtonClicked() {
        if (!panelType) {
            if (inline) {
                setTableState((prevState) => {
                    const minId = Math.min(...prevState.rows.map((row) => row.id));
                    const id = minId > 0 ? -1 : minId - 1;
                    return {
                        ...prevState,
                        rows: [...prevState.rows, { id, isNew: true, ...inline.columnsWithInitialValues }],
                        total: (prevState.total += 1),
                        rowModesModel: { ...prevState.rowModesModel, [id]: { mode: GridRowModes.Edit, fieldToFocus: inline.fieldToFocus } },
                    };
                });
            } else if (navigate?.to) {
                navigateTo(navigate.to, navigate.options);
            } else if (onClick) {
                onClick();
            }
        } else {
            renderFormPanel();
        }
    }

    function renderFormPanel() {
        if (panelType && !panelType?.togglePanel) {
            throw Error("Panel type adding record require togglePanel to be defined");
        }
        panelType?.togglePanel((prev) => !prev);
    }
};

const OptionsButton = ({ clearPreferences }: Pick<CustomGridToolbarProps, "clearPreferences">) => {
    const currentTheme = useTheme();
    const isPublicOnlyMode = !!currentTheme.entityTable?.publicOnly;
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const handleOnClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    }, []);
    const handleCloseMenu = useCallback(() => {
        setAnchorEl(null);
    }, []);

    if (isPublicOnlyMode) {
        return null;
    }
    return (
        <>
            <IconButton key="grid-toolbar-options-button" onClick={handleOnClick}>
                <FontAwesomeIcon icon={MirageTheme.icons.gearIcon} color={MirageTheme.palette.primary.main} style={MirageTheme.icons.sizeM} />
            </IconButton>
            <Menu id="gridOptionsMenu" anchorEl={anchorEl} keepMounted open={Boolean(anchorEl)} onClose={handleCloseMenu}>
                {getItems()}
            </Menu>
        </>
    );

    function getItems() {
        return [
            <MenuItem key="account-settings-menu-item" onClick={clearPreferences}>
                {/* <ListItemIcon>
                    <FontAwesomeIcon icon={MirageTheme.icons.closeIcon} />
                </ListItemIcon> */}
                <Typography>Reset preferences</Typography>
            </MenuItem>,
        ];
    }
};

interface NewRecordButtonProps extends Partial<IEntityTableAddRecord> {
    setTableState: React.Dispatch<React.SetStateAction<ITableState>>;
}

export type IEntityTableAddRecord = {
    inline?: IEntityTableAddInlineRecord;
    navigate?: { to: To; options?: NavigateOptions };
    panelType?: NewRecordPanel;
    onClick?: () => void;
    disabled?: boolean;
    caption?: string;
    isLoading?: boolean;
    icon?: IconDefinition;
};

type IEntityTableAddInlineRecord = {
    fieldToFocus?: string;
    columnsWithInitialValues?: {
        [column: string]: string;
    };
};

export type NewRecordPanel = {
    togglePanel: React.Dispatch<React.SetStateAction<boolean>>;
};

export type CustomGridToolbarOptions = {
    customButtons?: JSX.Element[];
    newRecord?: IEntityTableAddRecord;
    disableFilter?: boolean;
    disableColumns?: boolean;
    disableOptions?: boolean;
};

export default CustomGridToolbar;
